import { Search, Pencil, UserCheck } from 'lucide-react';
import { Input } from '@/components/ui/input';

const MembersTab = ({
  filteredMembers, memberSearch, setMemberSearch,
  selectedMemberIds, setSelectedMemberIds, toggleMemberSelect, toggleAllMembers, batchMemberDelete,
  colConfig, memberSort, toggleSort,
  memberVisibleCount, setMemberVisibleCount,
  setSelectedMember, setEditingMember,
  memberStats, isLoading,
}) => {
  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9B9589]" size={16} />
          <Input type="text" value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} placeholder="Search members by name, ID, email..." className="input-ainaa pl-11 h-11" data-testid="member-search-input" />
        </div>
        <span className="text-xs text-[#9B9589] font-medium">{filteredMembers.length} members</span>
        <colConfig.ColumnToggle />
      </div>

      {/* Batch toolbar for members */}
      {selectedMemberIds.size > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-[#0F1B2D] text-[#F5F3EE]" data-testid="member-batch-toolbar">
          <span className="text-xs font-bold">{selectedMemberIds.size} selected</span>
          <button onClick={batchMemberDelete} className="px-3 py-1.5 text-[10px] font-bold uppercase bg-red-600 hover:bg-red-700 text-white" data-testid="batch-delete-members">Delete</button>
          <button onClick={() => setSelectedMemberIds(new Set())} className="ml-auto px-3 py-1.5 text-[10px] font-bold uppercase border border-white/30 hover:bg-white/10">Clear</button>
        </div>
      )}

      <div className="border border-[#D8D3CB] bg-white overflow-x-auto">
        <table className="w-full" data-testid="members-table" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr className="border-b-2 border-[#D8D3CB]">
              <th className="px-3 py-3" style={{ width: '40px' }}>
                <input type="checkbox"
                  checked={filteredMembers.length > 0 && selectedMemberIds.size === filteredMembers.slice(0, memberVisibleCount).length}
                  onChange={() => toggleAllMembers(filteredMembers.slice(0, memberVisibleCount).map(m => m.ainaa_id))}
                  className="w-3.5 h-3.5 accent-[#C4A35A] cursor-pointer" data-testid="member-select-all"
                />
              </th>
              {colConfig.visibleCols.map(col => (
                <colConfig.ResizableHeader key={col.key} col={col} sortKey={col.key} currentSort={memberSort} onSort={toggleSort} />
              ))}
              <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[#9B9589]" style={{ width: '80px' }}>Edit</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={colConfig.visibleCols.length + 2} className="text-center py-12 text-[#9B9589] font-medium">Loading...</td></tr>
            ) : filteredMembers.length === 0 ? (
              <tr><td colSpan={colConfig.visibleCols.length + 2} className="text-center py-12"><UserCheck className="w-10 h-10 text-[#D8D3CB] mx-auto mb-3" /><p className="text-[#9B9589] font-medium text-sm">No members found</p></td></tr>
            ) : (
              filteredMembers.slice(0, memberVisibleCount).map((member) => (
                <tr key={member.ainaa_id} className="border-b border-[#D8D3CB]/50 hover:bg-emerald-50/30 cursor-pointer" onClick={() => setSelectedMember(member)}>
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox"
                      checked={selectedMemberIds.has(member.ainaa_id)}
                      onChange={() => toggleMemberSelect(member.ainaa_id)}
                      className="w-3.5 h-3.5 accent-[#C4A35A] cursor-pointer"
                    />
                  </td>
                  {colConfig.visibleCols.map(col => {
                    const w = colConfig.colWidths[col.key];
                    const style = w ? { width: `${w}px`, minWidth: `${w}px` } : {};
                    switch (col.key) {
                      case 'ainaa_id':
                        return <td key={col.key} className="px-4 py-3" style={style}><span className="text-[11px] font-mono font-bold text-emerald-700 bg-emerald-50 px-2 py-1 border border-emerald-200" data-testid={`member-id-${member.ainaa_id}`}>{member.ainaa_id}</span></td>;
                      case 'name':
                        return <td key={col.key} className="px-4 py-3" style={style}><span className="font-semibold text-sm text-[#0F1B2D] truncate block">{member.name}</span></td>;
                      case 'email':
                        return <td key={col.key} className="px-4 py-3" style={style}><span className="text-sm text-[#4A4640] truncate block">{member.email}</span></td>;
                      case 'profession':
                        return <td key={col.key} className="px-4 py-3" style={style}><span className="text-sm text-[#4A4640] truncate block">{member.profession}</span></td>;
                      case 'field':
                        return <td key={col.key} className="px-4 py-3" style={style}>{member.field ? <span className="text-xs text-[#4A4640] capitalize">{member.field.replace(/_/g, ' ')}</span> : <span className="text-[#D8D3CB]">—</span>}</td>;
                      case 'trust_score':
                        return <td key={col.key} className="px-4 py-3" style={style}>{member.trust_score != null ? <span className="text-xs font-bold text-[#C4A35A]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{member.trust_score}</span> : <span className="text-[#D8D3CB]">—</span>}</td>;
                      case 'country':
                        return <td key={col.key} className="px-4 py-3" style={style}>{member.country ? <span className="text-xs text-[#4A4640]">{member.country}</span> : <span className="text-[#D8D3CB]">—</span>}</td>;
                      case 'application_id':
                        return <td key={col.key} className="px-4 py-3" style={style}><span className="text-[11px] font-mono text-[#9B9589]">{member.application_id}</span></td>;
                      default:
                        return <td key={col.key} className="px-4 py-3" style={style}><span className="text-sm text-[#4A4640]">{member[col.key] ?? '—'}</span></td>;
                    }
                  })}
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => setEditingMember(member)} className="p-1.5 text-[#9B9589] hover:text-[#C4A35A] transition-colors" title="Edit" data-testid={`edit-member-${member.ainaa_id}`}><Pencil size={14} /></button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {filteredMembers.length > memberVisibleCount && (
          <div className="border-t border-[#D8D3CB] bg-emerald-50/30 p-3 text-center">
            <button onClick={() => setMemberVisibleCount(c => c + 50)} className="px-5 py-2 text-[11px] font-bold text-[#0F1B2D] uppercase tracking-wider border border-[#D8D3CB] hover:border-[#0F1B2D] bg-white transition-all inline-flex items-center gap-2" data-testid="members-show-more">
              Show More ({filteredMembers.length - memberVisibleCount} remaining)
            </button>
          </div>
        )}
        {filteredMembers.length > 0 && (
          <div className="border-t border-[#D8D3CB] bg-white px-4 py-2 text-right">
            <span className="text-[10px] text-[#9B9589] font-medium">Showing {Math.min(memberVisibleCount, filteredMembers.length)} of {filteredMembers.length}</span>
          </div>
        )}
      </div>

      {memberStats?.by_profession?.length > 0 && (
        <div className="mt-6 border border-[#D8D3CB] bg-white p-6">
          <h3 className="text-sm font-bold text-[#0F1B2D] uppercase tracking-wider mb-4">Members by Profession</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {memberStats.by_profession.map((item) => (
              <div key={item.profession} className="text-center p-3 bg-emerald-50 border border-emerald-100">
                <p className="font-['Playfair_Display'] text-xl font-semibold text-emerald-800">{item.count}</p>
                <p className="text-[9px] font-bold text-emerald-600 mt-1 capitalize uppercase tracking-wider">{item.profession}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MembersTab;
